import { useEffect, useState } from 'react';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { DictionaryService } from 'service/dictionary/dictionaryService';
import type { IRecipeNutrition } from '../../types/RecipeNutrition';
import { Accordion } from '../Accordion';
import { Text as Paragraph, Title } from '../Typography';
import {
  ColData,
  ColHeading,
  ColHeadList,
  Container,
  CustomTab,
  Disclaimer,
  Heading,
  Label,
  MainTitle,
  MobileLabel,
  Note,
  SubTitle,
  Table,
  Td,
  Th,
  Tr,
} from './styles';

interface INutritionData {
  superTitle: string;
  mainHeading: string;
  title: string;
  ingredientsTitle: string;
  descriptionTitle: string;
  note: string;
  disclaimer: string;
  servingReferenceQuantityText: string;
  servingReferenceQuantity: number;
  referenceQuantity: number;
  servingDescription: string;
  colEmpty?: boolean[];
  decimalSeparator: string;
  headings?: {
    perMeasure: string;
    perServing: string;
    intakePercentage: string;
  };
  colCheck?: {
    preparedPerMeasureAllEmpty: string;
    preparedPerServingAllEmpty: string;
    preparedIntakePercentageAllEmpty: string;
    unPreparedPerMeasureAllEmpty: string;
    unPreparedPerServingAllEmpty: string;
    unPreparedIntakePercentageAllEmpty: string;
  };
  body?: {
    energy?: {
      name: string;
      prepared?: IEnergy;
      unPrepared?: IEnergy;
    };
    nutrients?: INutrient[];
  };
}
interface IEnergy {
  kiloJoules?: INutrition;
  kiloCalories?: INutrition;
}
interface INutrient {
  name: string;
  prepared?: INutrition;
  unPrepared?: INutrition;
}

interface INutrition {
  name: string;
  perMeasure: number;
  perServing: number;
  intakePercentage: number;
}

export type { INutrient, INutrition, INutritionData };

export const RecipeNutrition = ({
  data,
  titles,
  decimalSeparator,
  serving,
}: {
  data: IRecipeNutrition[];
  titles: any;
  decimalSeparator: string;
  serving: string;
}) => {
  const name = 'RecipeNutrition';
  const nutritionItemsList = ['fat', 'saturatedFat', 'carbohydrate', 'sugar', 'protein', 'salt'];
  const decimalClean = (value: number) => String(value).replace('.', decimalSeparator);
  const getDBName = (str: string) => `${str.charAt(0).toUpperCase()}${str.slice(1)}InGrams`;
  const getNutritionData = () => {
    const title = (
      <Title tag="div" type="sm">
        {DictionaryService.getCopy('nutrition_information', titles) || 'Nutrition Information'}
      </Title>
    );
    const body = (
      <div>
        {serving && (
          <Note tag="div" type="lead">
            {DictionaryService.getCopy('portion_text', titles) || '1 Portion = '} {serving} g
          </Note>
        )}
        <span className={'hidden'}>{decimalClean(data[1]?.EnergyInKilocalories)} kcal</span>
        <Table>
          <tbody>
            <Tr>
              <Th />
              <Th>
                <Heading tag="div" type="lead">
                  {DictionaryService.getCopy('per_100g', titles) || ''}
                </Heading>
              </Th>
              <Th>
                <Heading tag="div" type="lead">
                  {DictionaryService.getCopy('per_portion', titles) || ''}
                </Heading>
              </Th>
            </Tr>
            <Tr line="odd">
              <Td>
                <Label tag="div" type="lg">
                  {DictionaryService.getCopy('energy', titles) || 'Energy'}
                </Label>
              </Td>
              <Td>
                <Paragraph tag="div" type="lg">
                  {decimalClean(data[0]?.EnergyInKilojoules)} kj
                  <br />
                  {decimalClean(data[0]?.EnergyInKilocalories)} kcal
                </Paragraph>
              </Td>
              <Td>
                <Paragraph tag="div" type="lg">
                  {decimalClean(data[1]?.EnergyInKilojoules)} kj
                  <br />
                  {decimalClean(data[1]?.EnergyInKilocalories)} kcal
                </Paragraph>
              </Td>
            </Tr>
            {nutritionItemsList?.map((item: string, index: number) => (
              // eslint-disable-next-line react/no-array-index-key
              <Tr key={`nutrition-item-${index}`} line={index % 2 === 0 ? 'even' : 'odd'}>
                <Td>
                  <Label tag="div" type="lg">
                    {DictionaryService.getCopy(item, titles)}
                  </Label>
                </Td>
                <Td>
                  {item === 'dietaryFiber' || item === 'salt' ? (
                    <Paragraph tag="div" type="lg">
                      {!!(data[0] as any)[getDBName(item)] &&
                        decimalClean((data[0] as any)[getDBName(item)]?.toFixed(2))}{' '}
                      g
                    </Paragraph>
                  ) : (
                    <Paragraph tag="div" type="lg">
                      <span>
                        {!!(data[0] as any)[getDBName(item)] &&
                          decimalClean((data[0] as any)[getDBName(item)]?.toFixed(2))}{' '}
                      </span>
                      <span>g</span>
                    </Paragraph>
                  )}
                </Td>
                <Td>
                  <Paragraph tag="div" type="lg">
                    {!!(data[1] as any)[getDBName(item)] &&
                      decimalClean((data[1] as any)[getDBName(item)]?.toFixed(2))}{' '}
                    g
                  </Paragraph>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
        <Disclaimer tag="p" type="sm">
          {DictionaryService.getCopy('NutritionalDisclaimer', titles) || ''}
        </Disclaimer>
      </div>
    );
    return { title, body };
  };

  const getNutritionDataMobile = () => {
    const title = (
      <Title tag="div" type="xxs">
        {DictionaryService.getCopy('nutrition_information', titles) || 'Nutrition Information'}
      </Title>
    );
    const body = (
      <div>
        {serving && (
          <Note tag="div" type="lead">
            {DictionaryService.getCopy('portion_text', titles) || '1 Portion = '} {serving} g
          </Note>
        )}
        <CustomTab>
          <Tabs>
            <div className="w-full overflow-y-auto top-headerM bg-white">
              <TabList>
                <Tab>
                  <Heading tag="div" type="lead" className="font-bold">
                    {DictionaryService.getCopy('per_100g', titles) || ''}
                  </Heading>
                </Tab>
                <Tab>
                  <Heading tag="div" type="lead" className="font-bold">
                    {DictionaryService.getCopy('per_portion', titles) || ''}
                  </Heading>
                </Tab>
              </TabList>
            </div>
            <ColHeading className="w-1/2 inline-block z-negative relative">
              <ColHeadList line="odd" className="h-90">
                <MobileLabel tag="div" type="md">
                  {DictionaryService.getCopy('energy', titles) || 'Energy'}
                </MobileLabel>
              </ColHeadList>
              {nutritionItemsList?.map((item: string, index: number) => (
                <ColHeadList
                  // eslint-disable-next-line react/no-array-index-key
                  key={`nutrition-head-${index}`}
                  line={index % 2 === 0 ? 'even' : 'odd'}
                >
                  <MobileLabel tag="div" type="md">
                    {DictionaryService.getCopy(item, titles)}
                  </MobileLabel>
                </ColHeadList>
              ))}
            </ColHeading>
            <TabPanel className="w-1/2 hidden z-negative relative">
              <ColData className="h-90" line="odd">
                <Paragraph tag="div" type="md">
                  {decimalClean(data[0]?.EnergyInKilojoules)} kj
                  <br />
                  {decimalClean(data[0]?.EnergyInKilocalories)}
                  kcal
                </Paragraph>
              </ColData>
              {nutritionItemsList?.map((item: string, index: number) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`nutrition-col-1-${index}`}>
                  {!!(data[0] as any)[getDBName(item)] && (
                    <ColData line={index % 2 === 0 ? 'even' : 'odd'}>
                      {item === 'dietaryFiber' || item === 'salt' ? (
                        <Paragraph tag="div" type="md">
                          {decimalClean((data[0] as any)[getDBName(item)]?.toFixed(2))} g
                        </Paragraph>
                      ) : (
                        <Paragraph tag="div" type="md">
                          <span itemProp={`${item}Content`}>
                            {decimalClean((data[0] as any)[getDBName(item)]?.toFixed(2))}{' '}
                          </span>
                          <span>g</span>
                        </Paragraph>
                      )}
                    </ColData>
                  )}
                </div>
              ))}
            </TabPanel>
            <TabPanel className="w-1/2 hidden z-negative relative">
              <ColData className="h-90" line="odd">
                <Paragraph tag="div" type="md">
                  {decimalClean(data[1]?.EnergyInKilojoules)} kj
                  <br />
                  {decimalClean(data[1]?.EnergyInKilocalories)} kcal
                </Paragraph>
              </ColData>
              {nutritionItemsList?.map((item: string, index: number) => (
                // eslint-disable-next-line react/no-array-index-key
                <ColData key={`nutrition-col-2-${index}`} line={index % 2 === 0 ? 'even' : 'odd'}>
                  <Paragraph tag="div" type="md">
                    {!!(data[1] as any)[getDBName(item)] &&
                      decimalClean((data[1] as any)[getDBName(item)]?.toFixed(2))}{' '}
                    g
                  </Paragraph>
                </ColData>
              ))}
            </TabPanel>
          </Tabs>
        </CustomTab>
        <Disclaimer tag="p" type="sm">
          {DictionaryService.getCopy('NutritionalDisclaimer', titles) || ''}
        </Disclaimer>
      </div>
    );
    return { title, body };
  };
  const [screenWidth, setScreenWidth] = useState<number>(0);
  const screenSize = () => {
    setScreenWidth(window.innerWidth);
  };

  useEffect(() => {
    screenSize();
    window.addEventListener('load', screenSize);
    window.addEventListener('resize', screenSize);
    return () => window.removeEventListener('resize', screenSize);
  }, []);
  return (
    <Container data-test="recipe-nutrition-container" data-print="hide">
      <SubTitle data-test="recipe-nutrition-sub-title" tag="div" type="lead">
        {DictionaryService.getCopy('additional_information', titles) || 'Additional Information'}
      </SubTitle>
      <MainTitle data-test="recipe-nutrition-main-title" tag="div" type="md">
        {DictionaryService.getCopy('know_a_little_more', titles) || 'Know a little more'}
      </MainTitle>
      {screenWidth <= 475 ? (
        <div className="sm:w-viewPortWidthMobie md:w-full pr-small">
          {data && (
            <Accordion
              data-test="recipe-nutrition-accordion-mobile"
              name={name}
              data={[getNutritionDataMobile()]}
              collapsed
            />
          )}
        </div>
      ) : (
        <>
          {data && (
            <Accordion
              data-test="recipe-nutrition-accordion-desktop"
              name={name}
              data={[getNutritionData()]}
              collapsed
            />
          )}
        </>
      )}
    </Container>
  );
};
